/**
 * Step: Daily Routine - Tagesablauf Analyse
 * 
 * Erfasst wie der Kunde seinen Tag verbringt:
 * - Schlaf (Stunden pro Nacht)
 * - Arbeit (Stunden pro Woche + Art der Arbeit)
 * - Freizeit (Sitzen, leichte Aktivität)
 * 
 * HINWEIS: Sport wird separat in Schritt 3 "Deine Sportarten" erfasst!
 */
import React, { useState, useEffect } from 'react';
import { Clock, Moon, Briefcase, Sofa, Activity, Watch, AlertTriangle, CheckCircle, Monitor, Store, Hammer } from 'lucide-react';
import { getSportIcon } from './StepSportsActivities';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const WORK_TYPES = [
  { id: 'sitzend', label: 'Sitzend', desc: 'Büro, Homeoffice, IT', icon: Monitor, kcal_factor: 1.0 },
  { id: 'stehend', label: 'Stehend / Gehend', desc: 'Verkauf, Pflege, Gastro', icon: Store, kcal_factor: 1.3 },
  { id: 'koerperlich', label: 'Körperlich schwer', desc: 'Bau, Handwerk, Lager', icon: Hammer, kcal_factor: 1.6 },
];

const StepDailyRoutine = ({ prefs, setPrefs, healthKitData }) => {
  const [routine, setRoutine] = useState({
    sleep_hours: prefs.daily_routine?.sleep_hours || 7,
    work_hours_per_week: prefs.daily_routine?.work_hours_per_week ?? prefs.daily_routine?.work_hours_per_week ?? (prefs.daily_routine?.work_hours ? prefs.daily_routine.work_hours * 5 : 40),
    work_type: prefs.daily_routine?.work_type || 'sitzend',
    leisure_sitting_hours: prefs.daily_routine?.leisure_sitting_hours || 5,
    leisure_light_activity_hours: prefs.daily_routine?.leisure_light_activity_hours || 4,
    apple_watch_synced: prefs.daily_routine?.apple_watch_synced || false
  });

  // Derive daily work hours from weekly (assume 5 workdays)
  const dailyWorkHours = routine.work_hours_per_week > 0 ? Math.round((routine.work_hours_per_week / 5) * 10) / 10 : 0;
  
  // 24h validation: sleep + derived daily work + leisure
  const totalHours = routine.sleep_hours + dailyWorkHours + 
    routine.leisure_sitting_hours + routine.leisure_light_activity_hours;
  const isValid = Math.abs(totalHours - 24) < 1.5; // Slightly more lenient since work is derived

  useEffect(() => {
    setPrefs(prev => ({
      ...prev,
      daily_routine: {
        ...routine,
        // Keep work_hours for backward compatibility (derived from weekly)
        work_hours: dailyWorkHours,
        // Map work_type to percentage equivalents for existing calculation
        work_sitting_percent: routine.work_type === 'sitzend' ? 80 : routine.work_type === 'stehend' ? 30 : 10,
        work_standing_percent: routine.work_type === 'sitzend' ? 15 : routine.work_type === 'stehend' ? 40 : 30,
        work_walking_percent: routine.work_type === 'sitzend' ? 5 : routine.work_type === 'stehend' ? 30 : 60,
      }
    }));
  }, [routine]);

  useEffect(() => {
    if (healthKitData?.sleep_hours && !routine.apple_watch_synced) {
      setRoutine(prev => ({ ...prev, sleep_hours: healthKitData.sleep_hours, apple_watch_synced: true }));
    }
  }, [healthKitData]);

  const updateRoutine = (field, value) => {
    setRoutine(prev => ({ ...prev, [field]: value }));
  };

  const autoBalanceLeisure = () => {
    const availableLeisure = 24 - routine.sleep_hours - dailyWorkHours;
    if (availableLeisure > 0) {
      setRoutine(prev => ({
        ...prev,
        leisure_sitting_hours: Math.round(availableLeisure * 0.55 * 10) / 10,
        leisure_light_activity_hours: Math.round(availableLeisure * 0.45 * 10) / 10
      }));
    }
  };

  return (
    <div className="space-y-6" data-testid="onboarding-step-daily-routine">
      {/* Header */}
      <div className="text-center mb-6">
        <Clock className="w-12 h-12 text-purple-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-white">Dein Tagesablauf</h3>
        <p className="text-zinc-400 text-sm">Wie verbringst du deine Zeit?</p>
      </div>

      {/* Sleep Section */}
      <div className="p-4 bg-indigo-500/10 rounded-xl border border-indigo-500/30">
        <div className="flex items-center gap-3 mb-4">
          <Moon className="w-6 h-6 text-indigo-400" />
          <div className="flex-1">
            <h4 className="font-semibold text-white">Schlaf</h4>
            <p className="text-xs text-zinc-400">Durchschnittliche Schlafdauer pro Nacht</p>
          </div>
          {routine.apple_watch_synced && (
            <Badge className="bg-yellow-500/20 text-yellow-400 gap-1">
              <Watch className="w-3 h-3" /> Apple Watch
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4">
          <Slider
            value={[routine.sleep_hours]}
            onValueChange={([val]) => updateRoutine('sleep_hours', val)}
            min={4}
            max={12}
            step={0.5}
            className="flex-1"
          />
          <div className="w-20 text-center">
            <span className="text-2xl font-bold text-indigo-400">{routine.sleep_hours}</span>
            <span className="text-sm text-zinc-400 ml-1">h</span>
          </div>
        </div>
      </div>

      {/* Work Section - NEW: Hours per Week + Work Type */}
      <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/30">
        <div className="flex items-center gap-3 mb-4">
          <Briefcase className="w-6 h-6 text-amber-400" />
          <div className="flex-1">
            <h4 className="font-semibold text-white">Arbeit</h4>
            <p className="text-xs text-zinc-400">Wöchentliche Arbeitszeit und Art der Arbeit</p>
          </div>
        </div>
        
        {/* Work Hours per Week */}
        <div className="mb-5">
          <Label className="text-sm text-zinc-400 mb-2 block">Arbeitsstunden pro Woche</Label>
          <div className="flex items-center gap-4">
            <Slider
              value={[routine.work_hours_per_week]}
              onValueChange={([val]) => updateRoutine('work_hours_per_week', val)}
              min={0}
              max={60}
              step={2.5}
              className="flex-1"
              data-testid="work-hours-per-week-slider"
            />
            <div className="w-24 text-center">
              <span className="text-2xl font-bold text-amber-400">{routine.work_hours_per_week}</span>
              <span className="text-sm text-zinc-400 ml-1">h/Wo</span>
            </div>
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">
            = ca. {dailyWorkHours}h pro Arbeitstag (bei 5-Tage-Woche)
          </p>
        </div>

        {/* Work Type Selection */}
        {routine.work_hours_per_week > 0 && (
          <div className="pt-3 border-t border-amber-500/20">
            <Label className="text-sm text-zinc-400 mb-3 block">Welche Art von Arbeit?</Label>
            <div className="space-y-2">
              {WORK_TYPES.map(wt => (
                <button
                  key={wt.id}
                  type="button"
                  onClick={() => updateRoutine('work_type', wt.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    routine.work_type === wt.id
                      ? 'bg-amber-500/20 border-amber-500/50 ring-1 ring-amber-500/30'
                      : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                  }`}
                  data-testid={`work-type-${wt.id}`}
                >
                  <div className="flex items-center gap-3">
                    <wt.icon className="w-5 h-5 text-amber-300 shrink-0" />
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${routine.work_type === wt.id ? 'text-amber-300' : 'text-white'}`}>
                        {wt.label}
                      </p>
                      <p className="text-xs text-zinc-500">{wt.desc}</p>
                    </div>
                    {routine.work_type === wt.id && (
                      <CheckCircle className="w-5 h-5 text-amber-400" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Leisure Section */}
      <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Sofa className="w-6 h-6 text-zinc-400" />
            <div>
              <h4 className="font-semibold text-white">Freizeit</h4>
              <p className="text-xs text-zinc-400">Restliche Zeit ausserhalb von Arbeit & Schlaf</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={autoBalanceLeisure}
            className="text-xs text-purple-400 hover:text-purple-300"
          >
            Auto-Verteilen
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm text-zinc-400 flex items-center gap-2">
                Sitzen (TV, Essen, Computer)
              </Label>
              <span className="text-sm font-medium text-zinc-300">{routine.leisure_sitting_hours}h</span>
            </div>
            <Slider
              value={[routine.leisure_sitting_hours]}
              onValueChange={([val]) => updateRoutine('leisure_sitting_hours', val)}
              min={0}
              max={12}
              step={0.5}
              className="flex-1"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm text-zinc-400 flex items-center gap-2">
                Leichte Aktivität (Haushalt, Spazieren)
              </Label>
              <span className="text-sm font-medium text-zinc-300">{routine.leisure_light_activity_hours}h</span>
            </div>
            <Slider
              value={[routine.leisure_light_activity_hours]}
              onValueChange={([val]) => updateRoutine('leisure_light_activity_hours', val)}
              min={0}
              max={10}
              step={0.5}
              className="flex-1"
            />
          </div>
          
          {/* Sport summary from Step 3 */}
          {prefs.sports_activities && prefs.sports_activities.length > 0 ? (
            <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
              <p className="text-xs text-yellow-400 font-medium mb-2">Sport (aus Schritt 3)</p>
              <div className="space-y-1">
                {prefs.sports_activities.map((sport, i) => {
                  const userWeight = parseFloat(prefs.weight_kg) || 70;
                  const IMULT = { leicht: 0.75, mittel: 1.0, intensiv: 1.35 };
                  const mult = IMULT[sport.intensity || 'mittel'] || 1.0;
                  const kcalPerSession = Math.round((sport.met || 5) * mult * userWeight * (sport.duration / 60));
                  const intensityLabel = { leicht: 'L', mittel: 'M', intensiv: 'I' }[sport.intensity || 'mittel'];
                  const SportIcon = getSportIcon(sport.id);
                  return (
                    <div key={i} className="flex justify-between text-xs text-zinc-300">
                      <span className="flex items-center gap-1"><SportIcon className="w-3 h-3 text-yellow-400 shrink-0" /> {sport.name} <span className="text-zinc-500">({intensityLabel})</span></span>
                      <span className="text-yellow-400">{sport.frequency}x/Wo · {kcalPerSession} kcal</span>
                    </div>
                  );
                })}
              </div>
              {(() => {
                const userWeight = parseFloat(prefs.weight_kg) || 70;
                const IMULT = { leicht: 0.75, mittel: 1.0, intensiv: 1.35 };
                const weeklyKcal = prefs.sports_activities.reduce((sum, s) => {
                  const mult = IMULT[s.intensity || 'mittel'] || 1.0;
                  return sum + Math.round((s.met || 5) * mult * userWeight * (s.duration / 60)) * s.frequency;
                }, 0);
                const dailyKcal = Math.round(weeklyKcal / 7);
                return (
                  <div className="mt-2 pt-2 border-t border-yellow-500/20 flex justify-between text-xs font-medium">
                    <span className="text-zinc-400">ca. täglich durch Sport</span>
                    <span className="text-yellow-400">+{dailyKcal} kcal/Tag</span>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <p className="text-xs text-blue-400">
                Keine Sportarten in Schritt 3 erfasst - du kannst dort jederzeit welche hinzufügen
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Validation Message */}
      {!isValid && (
        <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0" />
          <p className="text-sm text-orange-400">
            Die Tagesverteilung passt nicht ganz (Schlaf {routine.sleep_hours}h + Arbeit ~{dailyWorkHours}h + Freizeit {(routine.leisure_sitting_hours + routine.leisure_light_activity_hours).toFixed(1)}h = {totalHours.toFixed(1)}h). Passe die Freizeit an oder klicke "Auto-Verteilen".
          </p>
        </div>
      )}

      {isValid && (
        <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-yellow-400" />
          <p className="text-sm text-yellow-400">
            Perfekt! Dein Tagesablauf ist vollständig erfasst.
          </p>
        </div>
      )}
    </div>
  );
};

export default StepDailyRoutine;
